// The in-memory API's personal crews (M4-01; services/api/src/routes/agents/crews.ts, rules in
// services/api/src/domain/crews/rules.ts and store.ts), with the real routes' rules:
// - the `agents` add-on (and `memory`, which it bundles): off → 403 `feature_disabled`, grace →
//   writes 403 `feature_read_only`;
// - `crews/me/*` is the caller's own crew only: a person membership; an agency operator without
//   one gets 403. Every person has a crew, "{display_name}'s agent", made on first use;
// - autonomy defaults to `ask` + `as_agent`, sharing to `if_requester_can_see`; `alone` only
//   where a role skill policy of the owner's seats' roles allows the kind (seat work needs a
//   seat), `on_behalf_of` never with `never` (422); kinds listed twice are 422;
// - the cap may only lower the ceiling (422); resume while the cap is reached is 409
//   `budget_exhausted` (spend is always 0 here, so a cap of 0 is reached);
// - the role-default crew agent cannot be removed (409); at most 8 added agents (409);
// - the interview's seven questions; answers are kept, `complete` writes them into context
//   layers (each superseding the current one of its layer);
// - role skill policies and crew caps are managers' (403 otherwise);
// - `Idempotency-Key` (a UUID): the same key and request replays the stored 2xx response, the
//   same key with another request is 422 `idempotency_mismatch`.
// M4-03, the crew features (services/api/src/domain/crews/features.ts and the design's rules):
// - features: the daily brief (on/off, "HH:MM", ISO weekdays), inbox triage on/off and the
//   heartbeat RRULEs (each must parse with `@companyos/domain-work/recurrence`: 422 otherwise);
// - "Brief me now": the deterministic brief (`composeBrief`'s text, ported below) posted by the
//   crew into the owner's `#brief` (the chat twin), topic "Brief {date}", once a day (a repeat
//   returns the day's brief, 200); a crew that is not active gets 409 `crew_paused`;
// - proposals: a crew run's (the `crewRun.propose` rules, reached here through the test hook
//   `FakeApi.crewPropose`, since the twin has no run tokens): triage kinds need triage on (409
//   `triage_off`), autonomy `never` is 403 `autonomy_never`, `alone` (where allowed) applies at
//   once, `ask` waits for the owner; one pending proposal per inbox item; accepting applies it
//   (done and snooze on the owner's inbox, a reply posted by the crew in the item's thread, a
//   reminder on the owner's seat, a task's status), dismissing drops it; a proposal whose item
//   left the inbox meanwhile expires. A drafted reply's approval (M3-03) is not modelled: the
//   twin posts the reply on accept, as the approval's executor would;
// - reminders on the owner's seats (the first held seat by default; none: 422 `no_seat`);
// - role memory grants (OQ 37): a held seat's memory, the department of a team a held seat sits
//   in, the leadership memory of a held seat with reports; nothing by default; the whole set is
//   PUT and a subject not on offer is 422 `grant_not_allowed`.
import { parseRule } from "@companyos/domain-work/recurrence";
import type { OperationOutput, RouteId } from "@companyos/sdk";
import {
  type Ctx,
  type Handler,
  json,
  notFound,
  type Principal,
  problem,
  validation,
} from "./fake";
import type { CrewChatBridge, CrewChatRef } from "./fake-chat";
import { crewMembershipIdOf, uid } from "./fake-data";

type CrewView = OperationOutput<"crews.me">;
type Crew = CrewView["crew"];
type CrewAgent = OperationOutput<"crews.addAgent">;
type Layer = OperationOutput<"crews.putContextLayer">;
type Policy = OperationOutput<"roleSkillPolicies.put">;
type Cap = OperationOutput<"crewCaps.get">;
type ActionKind = CrewView["autonomy"][number]["action_kind"];
type Mode = CrewView["autonomy"][number]["mode"];
type Identity = CrewView["autonomy"][number]["identity_mode"];
type SubjectKind = CrewView["sharing"][number]["subject_kind"];
type Rule = CrewView["sharing"][number]["rule"];
type LayerName = Layer["layer"];
type Visibility = Layer["visibility"];
type Question = CrewView["interview"]["questions"][number];
type CrewBrief = OperationOutput<"crews.brief">;
type Proposal = OperationOutput<"crews.acceptProposal">;
type ProposalKind = Proposal["kind"];
type Reminder = OperationOutput<"crews.addReminder">;
type MemoryCopy = NonNullable<OperationOutput<"crews.hireAsDe">["copy"]>;
type Grants = OperationOutput<"crews.memoryGrants">;

/** The server's words (domain/memory/personal-grant.ts `PERSONAL_GRANT_UNBOUND`). */
const PERSONAL_UNBOUND =
  "Not available yet: Recall must first link your agent to you, and that link is waiting on approval. Until then your agent cannot be let into your personal memory (and holds no access to take back).";
type Grant = Grants["data"][number];
type Subject = Grants["available"][number];
export type InboxItem = OperationOutput<"inbox.list">["data"][number];

// The daily brief (services/api/src/domain/crews/features.ts `localClock`, `longDate`,
// `composeBrief`, ported: the twin's brief reads exactly like the API's) ------------------------

/** The local calendar date and "HH:MM" of `at` in `zone`. */
export function localClock(at: Date, zone: string): { date: string; time: string } {
  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone: zone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  }).formatToParts(at);
  const get = (t: string) => parts.find((p) => p.type === t)?.value ?? "";
  return {
    date: `${get("year")}-${get("month")}-${get("day")}`,
    time: `${get("hour")}:${get("minute")}`,
  };
}

const WEEKDAY_NAMES = [
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
  "Sunday",
];
const MONTH_NAMES = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

/** "Tuesday 20 October 2026" for a YYYY-MM-DD date. */
export function longDate(on: string): string {
  const [y, m, d] = on.split("-").map(Number) as [number, number, number];
  const day = new Date(Date.UTC(y, m - 1, d)).getUTCDay();
  return `${WEEKDAY_NAMES[(day + 6) % 7]} ${d} ${MONTH_NAMES[m - 1]} ${y}`;
}

export type BriefInput = {
  on: string;
  inbox: { unread: number; top: readonly string[] };
  tasks: { dueOrOverdue: number; top: readonly { title: string; due: string | null }[] };
  approvalsWaiting: number;
  reminders: readonly { text: string; at: string }[];
  proposalsPending: number;
};

const BRIEF_LIST = 5;
const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;
const clip = (s: string, n = 120) => (s.length > n ? `${s.slice(0, n - 1)}…` : s);

/** The brief as Markdown; sections only when they have something in them. */
export function composeBrief(b: BriefInput): string {
  const out: string[] = [`## Brief for ${longDate(b.on)}`];
  if (b.inbox.unread > 0) {
    out.push("", `**Inbox:** ${plural(b.inbox.unread, "unread item")}`);
    for (const s of b.inbox.top.slice(0, BRIEF_LIST)) out.push(`- ${clip(s)}`);
  }
  if (b.tasks.dueOrOverdue > 0) {
    out.push("", `**Tasks due or overdue:** ${b.tasks.dueOrOverdue}`);
    for (const t of b.tasks.top.slice(0, BRIEF_LIST))
      out.push(`- ${clip(t.title)}${t.due ? ` (due ${t.due})` : ""}`);
  }
  if (b.approvalsWaiting > 0) out.push("", `**Approvals waiting on you:** ${b.approvalsWaiting}`);
  if (b.reminders.length > 0) {
    out.push("", `**Reminders today:** ${b.reminders.length}`);
    for (const r of b.reminders.slice(0, BRIEF_LIST)) out.push(`- ${r.at} ${clip(r.text)}`);
  }
  if (b.proposalsPending > 0)
    out.push(
      "",
      `**Your agent's proposals:** ${plural(b.proposalsPending, "suggestion")} waiting in your Inbox`,
    );
  if (out.length === 1) out.push("", "Nothing needs you today.");
  return out.join("\n");
}

/** The crew action kind (autonomy) each proposal kind needs (features.ts `actionKindOf`). */
const PROPOSAL_ACTION: Record<ProposalKind, ActionKind> = {
  done: "inbox_triage",
  snooze: "inbox_triage",
  reply: "message",
  remind: "reminder",
  task_update: "task_change",
};
const TRIAGE: ReadonlySet<ProposalKind> = new Set(["done", "snooze", "reply"]);
const BUCKETS = ["seat", "department", "leadership"] as const;
const HHMM = /^([01]\d|2[0-3]):[0-5]\d$/;
const CREW_TEXT = 500;
const CREW_DRAFT = 10_000;

// The contract enums (@companyos/schema enums.ts), mirrored as labels.ts does.
export const ACTION_KINDS: readonly ActionKind[] = [
  "message",
  "task_change",
  "page",
  "external_action",
  "delegation",
  "reminder",
  "inbox_triage",
];
export const AUTONOMY_MODES: readonly Mode[] = ["alone", "ask", "never"];
export const IDENTITY_MODES: readonly Identity[] = ["as_agent", "on_behalf_of"];
export const SUBJECT_KINDS: readonly SubjectKind[] = [
  "task",
  "message",
  "dm",
  "calendar",
  "memory",
  "run",
];
export const SHARING_RULES: readonly Rule[] = [
  "never",
  "status_only",
  "if_requester_can_see",
  "always",
];
export const LAYERS: readonly LayerName[] = [
  "manual",
  "identity",
  "business",
  "priorities",
  "voice",
  "connections",
  "decisions",
  "captures",
  "audits",
];
const VISIBILITIES: readonly Visibility[] = ["owner_only", "manager", "company"];
const PERIODS = ["day", "week", "month"] as const;
const SEAT_WORK: ReadonlySet<ActionKind> = new Set([
  "task_change",
  "page",
  "external_action",
  "delegation",
]);
const MAX_CREW_AGENTS = 8;
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const STRENGTH = { disabled: 0, grace: 1, trial: 2, active: 3 } as const;

/** The interview (rules.ts `interviewQuestions`), worded for the owner's seats and company. */
export function interviewQuestions(company: string, seats: readonly string[]): Question[] {
  const role = seats.length > 0 ? seats.join(" and ") : "your work";
  return [
    {
      id: "identity",
      prompt: `What do you do as ${role} at ${company}, and who is it for?`,
      hint: "Two or three sentences: the outcome you own and the people who rely on it.",
      layer: "identity",
      verbatim: false,
    },
    {
      id: "voice",
      prompt: "Paste one or two things you wrote recently, exactly as you sent them.",
      hint: "An email or a message. Paste it as sent; a description of your style is not a sample.",
      layer: "voice",
      verbatim: true,
    },
    {
      id: "priorities",
      prompt: "What are your two or three priorities for the next 90 days?",
      hint: "Be specific: what is different when each one is done?",
      layer: "priorities",
      verbatim: false,
    },
    {
      id: "business",
      prompt: `Where does your work create value for ${company}?`,
      hint: "The clients, deals or numbers that move because of it.",
      layer: "business",
      verbatim: false,
    },
    {
      id: "channels",
      prompt: "Where do people reach you, and where should your agent never post?",
      hint: "Channels, email, DMs. Nothing is connected now; your agent only notes where they are.",
      layer: "connections",
      verbatim: false,
    },
    {
      id: "documents",
      prompt: "Where do your meetings, notes and documents live?",
      hint: "Calendars, drives, boards. Again, nothing is connected yet.",
      layer: "connections",
      verbatim: false,
    },
    {
      id: "time_sink",
      prompt: "What takes most of your week that you would hand off, and where is it tracked?",
      hint: "The one thing your agent should help with first.",
      layer: "identity",
      verbatim: false,
    },
  ];
}

/** rules.ts `layersFromAnswers`: per layer, its questions and answers in interview order. */
function layersFromAnswers(questions: Question[], answers: Record<string, string>) {
  const out = new Map<LayerName, string>();
  for (const q of questions) {
    const a = answers[q.id]?.trim();
    if (!a) continue;
    const part = `${q.prompt}\n${a}`;
    const prev = out.get(q.layer);
    out.set(q.layer, prev ? `${prev}\n\n${part}` : part);
  }
  return out;
}

type AutonomyRow = {
  action_kind: ActionKind;
  mode: Mode;
  identity_mode: Identity;
  constraints: Record<string, unknown>;
};

type CrewState = {
  crew: Crew;
  name: string;
  agents: CrewAgent[];
  autonomy: AutonomyRow[];
  sharing: { subject_kind: SubjectKind; rule: Rule }[];
  /** Every layer row, append-only; the current ones are those nothing supersedes. */
  layers: Layer[];
  /** M4-03: proposals (newest last), the day's briefs by date, grants (revoked ones kept). */
  proposals: Proposal[];
  briefs: Map<string, CrewBrief>;
  grants: Grant[];
};

type Ceiling = {
  kinds: Set<ActionKind>;
  packs: Set<string>;
  defaults: string[];
  budget: number | null;
  holdsSeat: boolean;
};

export type CrewDeps = {
  now: () => Date;
  /**
   * D12 consent step: `unbound` (the server today: crews are not linked to their person in
   * Recall, an open decision), `bound` (test-only: it works), `no_recall` (memory not set up).
   */
  personalConsent?: "unbound" | "bound" | "no_recall";
  /** The seats (title, role) `membershipId` holds in the company, open ones only. */
  seatsHeld: (p: Principal, membershipId: string) => { title: string; role_id: string }[];
  /** Whether the company has this role (role skill policies are per role). */
  roleExists: (companyId: string, roleId: string) => boolean;
  // M4-03 (optional: without them the features answer as for a person with no seat, no inbox
  // and no chat) -------------------------------------------------------------------------------
  /** The seats `membershipId` holds (ids), and whether each supervises another seat. */
  ownerSeats?: (
    p: Principal,
    membershipId: string,
  ) => { id: string; title: string; hasReports: boolean }[];
  /** The teams the given seats are members of. */
  teamsOfSeats?: (p: Principal, seatIds: readonly string[]) => { id: string; name: string }[];
  /** The chat twin: `#brief` and the crew's posts. */
  chat?: CrewChatBridge;
  /** The caller's own inbox (the caller is the crew's owner), as the inbox routes answer it. */
  inbox?: {
    /** Every item: open, snoozed and done. */
    list: (ctx: Ctx) => InboxItem[];
    done: (ctx: Ctx, id: string) => boolean;
    snooze: (ctx: Ctx, id: string, until: string) => boolean;
  };
  /** Tasks as the owner sees them (Work): visible, and a status change or a comment. */
  tasks?: {
    visible: (ctx: Ctx, id: string) => boolean;
    update: (ctx: Ctx, id: string, change: { status?: string; comment?: string }) => boolean;
  };
  // M7-05 (optional: without it `crews.hireAsDe` answers 404) ----------------------------------
  /**
   * Hire the crew as a DE: the seat (as `seats.create`, its supervisor required as
   * `digitalEmployees.create`) and the DE on it. A refusal is the twins' own problem response.
   */
  hire?: (p: Principal, h: HireRequest) => Response | { seat_id: string; de_membership_id: string };
};

const isObj = (v: unknown): v is Record<string, unknown> =>
  typeof v === "object" && v !== null && !Array.isArray(v);
const unknownKeys = (b: Record<string, unknown>, allowed: string[]) =>
  Object.keys(b).filter((k) => !allowed.includes(k));
const isInt = (v: unknown) => typeof v === "number" && Number.isInteger(v);
const minOf = (...xs: (number | null)[]): number | null =>
  xs.reduce<number | null>((m, x) => (x === null ? m : m === null || x < m ? x : m), null);

function problemWith(status: number, body: Record<string, unknown>): Response {
  return new Response(
    JSON.stringify({ request_id: `fake-${Math.random().toString(36).slice(2, 10)}`, ...body }),
    { status, headers: { "content-type": "application/problem+json" } },
  );
}

const forbidden = (detail?: string) => problem(403, "forbidden", "Forbidden", detail);

export function createCrewHandlers(deps: CrewDeps): Partial<Record<RouteId, Handler>> {
  return createCrewFake(deps).handlers;
}

export type CrewFake = {
  handlers: Partial<Record<RouteId, Handler>>;
  /**
   * `crewRun.propose` as the owner's crew run would call it (the twin has no run tokens): `ctx`
   * is the owner's (principal, me), `ctx.body` the `CrewProposalCreate`.
   */
  propose: (ctx: Ctx) => Response;
  /** M7-05: the memory copy of a DE hired from a crew (its Recall agent panel), or null. */
  copyOf: (companyId: string, deMembershipId: string) => MemoryCopy | null;
};

/** M7-05: what `crews.hireAsDe` asks the structure and DE twins to make. */
export type HireRequest = {
  display_name: string;
  template_key: string;
  seat: { role_id: string; title: string; supervisor_seat_id: string };
  crew_membership_id: string;
};

export function createCrewFake(deps: CrewDeps): CrewFake {
  const crews = new Map<string, CrewState>();
  const policies = new Map<string, Policy[]>();
  const caps = new Map<string, Cap>();
  const idem = new Map<string, { hash: string; status: number; body: string }>();
  let seq = 0;
  const at = () => deps.now().toISOString();
  const next = (group: number) => uid(group, ++seq);

  /** The weakest status of `agents` and `memory` (its dependency), as the server's gate. */
  function gate(p: Principal, write: boolean): Response | null {
    const map = p.me.entitlements[p.company.id] ?? {};
    const statuses = (["agents", "memory"] as const).map(
      (k) => (map as Record<string, keyof typeof STRENGTH | undefined>)[k] ?? "disabled",
    );
    const status = statuses.reduce((a, b) => (STRENGTH[b] < STRENGTH[a] ? b : a));
    if (status === "disabled")
      return problemWith(403, {
        type: "https://companyos.app/errors/feature_disabled",
        title: "Feature not enabled",
        status: 403,
        code: "feature_disabled",
        feature: "agents",
        detail: "The agents add-on is not enabled for this company.",
      });
    if (status === "grace" && write)
      return problem(403, "feature_read_only", "Read-only", "The agents add-on is read-only.");
    return null;
  }

  /** D12: each crew's grant/revoke log (oldest first), and why granting is unavailable now. */
  const personalLog = new Map<string, OperationOutput<"crews.personalMemory">["log"]>();
  const personalUnavailable = (): string | null =>
    deps.personalConsent === "bound"
      ? null
      : deps.personalConsent === "no_recall"
        ? "Memory is not set up for you yet."
        : PERSONAL_UNBOUND;

  /** The caller's person membership in the company (null: none, e.g. an agency operator). */
  const personOf = (p: Principal) =>
    p.me.memberships.find((m) => m.company_id === p.company.id && m.kind === "person") ?? null;

  function ceilingOf(p: Principal, ownerId: string): Ceiling & { seats: string[] } {
    const held = deps.seatsHeld(p, ownerId);
    const roleIds = new Set(held.map((s) => s.role_id));
    const mine = (policies.get(p.company.id) ?? []).filter((x) => roleIds.has(x.role_id));
    const c: Ceiling = {
      kinds: new Set(),
      packs: new Set(),
      defaults: [],
      budget: null,
      holdsSeat: held.length > 0,
    };
    const defaults = new Set<string>();
    for (const x of mine) {
      for (const k of x.allowed_action_kinds) c.kinds.add(k);
      for (const s of x.allowed_skill_pack_ids) c.packs.add(s);
      for (const s of x.default_skill_pack_ids) defaults.add(s);
      c.budget = minOf(c.budget, x.budget_money_cents);
    }
    c.defaults = [...defaults].sort();
    return { ...c, seats: held.map((s) => s.title).sort() };
  }

  const aloneAllowed = (c: Ceiling, kind: ActionKind) =>
    c.kinds.has(kind) && (c.holdsSeat || !SEAT_WORK.has(kind));

  function capsOf(companyId: string): Cap {
    const t = new Date(0).toISOString();
    return (
      caps.get(companyId) ?? {
        company_id: companyId,
        per_crew_cap_minor: null,
        company_cap_minor: null,
        period_type: "month",
        actor_kind: null,
        actor_membership_id: null,
        created_at: t,
        updated_at: t,
      }
    );
  }

  const writer = (p: Principal) => {
    const m = personOf(p);
    return {
      actor_kind: (m ? "person" : p.operator ? "agency_user" : "person") as Crew["actor_kind"],
      actor_membership_id: m?.membership_id ?? null,
    };
  };

  /** The owner's crew, made on first use (the server's `crews.setup` consumer). */
  function crewOf(p: Principal, ownerId: string): CrewState {
    const key = `${p.company.id}:${ownerId}`;
    let s = crews.get(key);
    if (!s) {
      const t = at();
      const name = `${p.me.user.name.trim() || p.me.user.email}'s agent`;
      // Stable per (company, owner): the crew's own ids do not depend on the call order.
      const n =
        (Number.parseInt(p.company.id.slice(-4), 16) % 4096) * 2 ** 32 +
        Number.parseInt(ownerId.slice(-8), 16);
      const membershipId = crewMembershipIdOf(p.company.id, ownerId);
      const c = ceilingOf(p, ownerId);
      const system = { actor_kind: "system" as const, actor_membership_id: null };
      s = {
        name,
        crew: {
          company_id: p.company.id,
          membership_id: membershipId,
          owner_membership_id: ownerId,
          personal_space_id: uid(0xc7e1, n),
          brief_channel_id: uid(0xc7e2, n),
          context_pack_ref: null,
          onboarding_state: {},
          heartbeat_schedule: [],
          max_concurrency: 1,
          model_policy: {},
          cap_minor: null,
          state: "active",
          pause_reason: null,
          // M4-03's defaults (migration 0028).
          brief_enabled: false,
          brief_time: "07:00",
          brief_days: [1, 2, 3, 4, 5],
          last_brief_on: null,
          triage_enabled: false,
          ...system,
          created_at: t,
          updated_at: t,
        },
        agents: [
          {
            company_id: p.company.id,
            id: uid(0xc7e3, n),
            crew_membership_id: membershipId,
            agent_profile_id: uid(0xc7e4, n),
            name,
            skill_pack_ids: c.defaults,
            source: "role_default",
            enabled: true,
            ...system,
            created_at: t,
            updated_at: t,
          },
        ],
        autonomy: [],
        sharing: [],
        layers: [],
        proposals: [],
        briefs: new Map(),
        grants: [],
      };
      crews.set(key, s);
      // setup.ts `ensureCrew`: the owner's personal space and `#brief` come with the crew.
      deps.chat?.ensureBrief(chatRef(p, s));
    }
    return s;
  }

  /** The crew as the chat twin sees it. */
  function chatRef(p: Principal, s: CrewState): CrewChatRef {
    return {
      companyId: p.company.id,
      ownerId: s.crew.owner_membership_id,
      ownerName: p.me.user.name.trim() || p.me.user.email,
      crewId: s.crew.membership_id,
      crewAgentId: s.agents[0]?.id ?? s.crew.membership_id,
      crewName: s.name,
      spaceId: s.crew.personal_space_id,
      channelId: s.crew.brief_channel_id,
    };
  }

  const currentLayers = (s: CrewState) =>
    s.layers
      .filter((l) => !s.layers.some((n) => n.supersedes_id === l.id))
      .sort((a, b) => LAYERS.indexOf(a.layer) - LAYERS.indexOf(b.layer));

  function view(p: Principal, s: CrewState, ownerId: string): CrewView {
    const c = ceilingOf(p, ownerId);
    const companyCaps = capsOf(p.company.id);
    const ceiling = minOf(companyCaps.per_crew_cap_minor, c.budget);
    const answers = Object.fromEntries(
      Object.entries(s.crew.onboarding_state.answers ?? {}).map(([k, v]) => [k, v.answer]),
    );
    return {
      crew: structuredClone(s.crew),
      agent: { membership_id: s.crew.membership_id, display_name: s.name, status: "active" },
      crew_agents: s.agents.filter((a) => a.enabled).map((a) => structuredClone(a)),
      autonomy: ACTION_KINDS.map((kind) => {
        const r = s.autonomy.find((x) => x.action_kind === kind);
        return {
          action_kind: kind,
          mode: r?.mode ?? "ask",
          identity_mode: r?.identity_mode ?? "as_agent",
          constraints: r?.constraints ?? {},
          alone_allowed: aloneAllowed(c, kind),
          source: r ? ("policy" as const) : ("default" as const),
        };
      }),
      sharing: SUBJECT_KINDS.map((kind) => {
        const r = s.sharing.find((x) => x.subject_kind === kind);
        return {
          subject_kind: kind,
          rule: r?.rule ?? "if_requester_can_see",
          source: r ? ("policy" as const) : ("default" as const),
        };
      }),
      context_layers: currentLayers(s).map((l) => structuredClone(l)),
      interview: {
        questions: interviewQuestions(p.company.name, c.seats),
        answers,
        complete: Boolean(s.crew.onboarding_state.scaffolded_at),
      },
      caps: {
        period_type: companyCaps.period_type,
        currency: p.company.reporting_currency as CrewView["caps"]["currency"],
        cap_minor: s.crew.cap_minor,
        ceiling_minor: ceiling,
        effective_minor: minOf(s.crew.cap_minor, ceiling),
        spent_minor: 0,
      },
      features: {
        triage_enabled: s.crew.triage_enabled,
        brief: {
          enabled: s.crew.brief_enabled,
          time: s.crew.brief_time,
          days: [...s.crew.brief_days],
          last_on: s.crew.last_brief_on,
        },
        heartbeat_schedule: [...s.crew.heartbeat_schedule],
      },
    };
  }

  function writeLayer(
    p: Principal,
    s: CrewState,
    layer: LayerName,
    body: string,
    visibility: Visibility,
    source: "onboarding" | "owner",
    sourceId: string | null,
  ): Layer {
    const t = at();
    const current = currentLayers(s).find((l) => l.layer === layer);
    const row: Layer = {
      company_id: p.company.id,
      id: next(0xc7e5),
      crew_membership_id: s.crew.membership_id,
      layer,
      body,
      object_key: null,
      visibility,
      confirmed_by_owner: true,
      as_of: t,
      source_ref: { kind: source, id: sourceId },
      supersedes_id: current?.id ?? null,
      ...writer(p),
      created_at: t,
      updated_at: t,
    };
    s.layers.push(row);
    return row;
  }

  const touch = (p: Principal, s: CrewState) => {
    Object.assign(s.crew, writer(p), { updated_at: at() });
  };

  /**
   * A `crews/me/*` handler: the gate, the owner's person membership, `Idempotency-Key` replay
   * for writes, then `run` with the owner's crew.
   */
  function own(
    id: RouteId,
    write: boolean,
    run: (ctx: Ctx, p: Principal, s: CrewState, ownerId: string) => Response,
  ): Handler {
    return (ctx) => {
      const p = ctx.principal;
      if (!p) return notFound();
      const off = gate(p, write);
      if (off) return off;
      const m = personOf(p);
      if (!m) return forbidden("only a person's own session manages a crew");
      const s = crewOf(p, m.membership_id);
      return write
        ? idempotent(ctx, id, `${p.company.id}:${p.me.user.id}`, () =>
            run(ctx, p, s, m.membership_id),
          )
        : run(ctx, p, s, m.membership_id);
    };
  }

  /** A managers' handler (`authorize(c, "manager")`). */
  function managers(
    id: RouteId,
    write: boolean,
    run: (ctx: Ctx, p: Principal) => Response,
  ): Handler {
    return (ctx) => {
      const p = ctx.principal;
      if (!p) return notFound();
      const off = gate(p, write);
      if (off) return off;
      if (!p.manager) return forbidden("managers only");
      return write && id !== "roleSkillPolicies.delete"
        ? idempotent(ctx, id, `${p.company.id}:${p.me.user.id}`, () => run(ctx, p))
        : run(ctx, p);
    };
  }

  function idempotent(ctx: Ctx, id: RouteId, scope: string, run: () => Response): Response {
    const key = ctx.headers.get("idempotency-key");
    if (key === null) return run();
    if (!UUID.test(key)) return validation("Idempotency-Key: must be a UUID");
    const hash = `${id}\n${ctx.url.pathname}\n${JSON.stringify(ctx.body ?? null)}`;
    const stored = idem.get(`${scope}:${key}`);
    if (stored) {
      if (stored.hash !== hash)
        return problem(
          422,
          "idempotency_mismatch",
          "Idempotency-Key reused with a different request",
        );
      return new Response(stored.body, {
        status: stored.status,
        headers: { "content-type": "application/json", "idempotent-replayed": "true" },
      });
    }
    const res = run();
    const body = texts.get(res);
    if (res.status >= 200 && res.status < 300 && body !== undefined)
      idem.set(`${scope}:${key}`, { hash, status: res.status, body });
    return res;
  }

  /** The text of each `reply()` (handlers are synchronous; a Response body is read async). */
  const texts = new WeakMap<Response, string>();
  /** `json()` that remembers its text, for the idempotency store. */
  const reply = (body: unknown, status = 200): Response => {
    const res = json(body, status);
    texts.set(res, JSON.stringify(body));
    return res;
  };

  // Body checks (the contract's zod schemas, by hand as the other fakes do) ---------------------

  function checkPatch(b: unknown): string | null {
    if (!isObj(b)) return "body: expected an object";
    const extra = unknownKeys(b, [
      "display_name",
      "heartbeat_schedule",
      "max_concurrency",
      "model_policy",
      "cap_minor",
    ]);
    if (extra.length) return `${extra[0]}: unrecognized key`;
    if (b.display_name !== undefined) {
      if (typeof b.display_name !== "string" || !b.display_name.trim())
        return "display_name: must not be empty";
      if (b.display_name.trim().length > 200) return "display_name: at most 200 characters";
    }
    if (b.heartbeat_schedule !== undefined) {
      const h = b.heartbeat_schedule;
      if (!Array.isArray(h) || h.length > 8 || h.some((x) => typeof x !== "string" || !x.trim()))
        return "heartbeat_schedule: at most 8 non-empty entries";
    }
    if (b.max_concurrency !== undefined) {
      const n = b.max_concurrency;
      if (!isInt(n) || (n as number) < 1 || (n as number) > 8)
        return "max_concurrency: a whole number from 1 to 8";
    }
    if (b.model_policy !== undefined && !isObj(b.model_policy))
      return "model_policy: expected an object";
    if (b.cap_minor !== undefined && b.cap_minor !== null) {
      if (!isInt(b.cap_minor) || (b.cap_minor as number) < 0)
        return "cap_minor: a whole amount of zero or more";
    }
    return null;
  }

  function checkAutonomy(b: unknown, c: Ceiling): string | AutonomyRow[] {
    if (!isObj(b)) return "body: expected an object";
    const extra = unknownKeys(b, ["policies"]);
    if (extra.length) return `${extra[0]}: unrecognized key`;
    if (!Array.isArray(b.policies) || b.policies.length > ACTION_KINDS.length)
      return `policies: at most ${ACTION_KINDS.length} entries`;
    const out: AutonomyRow[] = [];
    const seen = new Set<string>();
    const problems: string[] = [];
    for (const [i, raw] of b.policies.entries()) {
      if (!isObj(raw)) return `policies.${i}: expected an object`;
      const bad = unknownKeys(raw, ["action_kind", "mode", "identity_mode", "constraints"]);
      if (bad.length) return `policies.${i}.${bad[0]}: unrecognized key`;
      if (!ACTION_KINDS.includes(raw.action_kind as ActionKind))
        return `policies.${i}.action_kind: invalid option`;
      if (!AUTONOMY_MODES.includes(raw.mode as Mode)) return `policies.${i}.mode: invalid option`;
      const identity = raw.identity_mode ?? "as_agent";
      if (!IDENTITY_MODES.includes(identity as Identity))
        return `policies.${i}.identity_mode: invalid option`;
      const constraints = raw.constraints ?? {};
      if (!isObj(constraints)) return `policies.${i}.constraints: expected an object`;
      const row: AutonomyRow = {
        action_kind: raw.action_kind as ActionKind,
        mode: raw.mode as Mode,
        identity_mode: identity as Identity,
        constraints,
      };
      // rules.ts `checkAutonomy`.
      if (seen.has(row.action_kind)) problems.push(`policies.${i}.action_kind: listed twice`);
      seen.add(row.action_kind);
      if (row.mode === "alone" && !aloneAllowed(c, row.action_kind))
        problems.push(
          `policies.${i}.mode: ${
            c.kinds.has(row.action_kind)
              ? `${row.action_kind} needs a seat: the owner cannot do it either`
              : `no role skill policy of yours allows ${row.action_kind} alone`
          }`,
        );
      if (row.identity_mode === "on_behalf_of" && row.mode === "never")
        problems.push(
          `policies.${i}.identity_mode: acting in your name needs the action to be allowed`,
        );
      out.push(row);
    }
    return problems.length ? problems.join("; ") : out;
  }

  function checkSharing(b: unknown): string | CrewState["sharing"] {
    if (!isObj(b)) return "body: expected an object";
    const extra = unknownKeys(b, ["rules"]);
    if (extra.length) return `${extra[0]}: unrecognized key`;
    if (!Array.isArray(b.rules) || b.rules.length > SUBJECT_KINDS.length)
      return `rules: at most ${SUBJECT_KINDS.length} entries`;
    const out: CrewState["sharing"] = [];
    const seen = new Set<string>();
    for (const [i, raw] of b.rules.entries()) {
      if (!isObj(raw)) return `rules.${i}: expected an object`;
      const bad = unknownKeys(raw, ["subject_kind", "rule"]);
      if (bad.length) return `rules.${i}.${bad[0]}: unrecognized key`;
      if (!SUBJECT_KINDS.includes(raw.subject_kind as SubjectKind))
        return `rules.${i}.subject_kind: invalid option`;
      if (!SHARING_RULES.includes(raw.rule as Rule)) return `rules.${i}.rule: invalid option`;
      if (seen.has(raw.subject_kind as string)) return `rules.${i}.subject_kind: listed twice`;
      seen.add(raw.subject_kind as string);
      out.push({ subject_kind: raw.subject_kind as SubjectKind, rule: raw.rule as Rule });
    }
    return out;
  }

  const handlers: Partial<Record<RouteId, Handler>> = {
    "crews.me": own("crews.me", false, (_ctx, p, s, ownerId) => json(view(p, s, ownerId))),

    "crews.update": own("crews.update", true, ({ body }, p, s, ownerId) => {
      const bad = checkPatch(body);
      if (bad) return validation(bad);
      const b = body as Record<string, unknown>;
      if (typeof b.cap_minor === "number") {
        const ceiling = view(p, s, ownerId).caps.ceiling_minor;
        if (ceiling !== null && b.cap_minor > ceiling)
          return validation(`cap_minor: the ceiling is ${ceiling}; a cap may only lower it`);
      }
      const same = (x: unknown, y: unknown) => JSON.stringify(x) === JSON.stringify(y);
      let changed = false;
      if (
        Array.isArray(b.heartbeat_schedule) &&
        !same(b.heartbeat_schedule, s.crew.heartbeat_schedule)
      ) {
        s.crew.heartbeat_schedule = b.heartbeat_schedule as string[];
        changed = true;
      }
      if (typeof b.max_concurrency === "number" && b.max_concurrency !== s.crew.max_concurrency) {
        s.crew.max_concurrency = b.max_concurrency;
        changed = true;
      }
      if (isObj(b.model_policy) && !same(b.model_policy, s.crew.model_policy)) {
        s.crew.model_policy = b.model_policy;
        changed = true;
      }
      if (b.cap_minor !== undefined && b.cap_minor !== s.crew.cap_minor) {
        s.crew.cap_minor = b.cap_minor as number | null;
        changed = true;
      }
      if (changed) touch(p, s);
      if (typeof b.display_name === "string") s.name = b.display_name.trim();
      return reply(view(p, s, ownerId));
    }),

    "crews.putAutonomy": own("crews.putAutonomy", true, ({ body }, p, s, ownerId) => {
      const rows = checkAutonomy(body, ceilingOf(p, ownerId));
      if (typeof rows === "string") return validation(rows);
      s.autonomy = rows;
      return reply(view(p, s, ownerId));
    }),

    "crews.putSharing": own("crews.putSharing", true, ({ body }, p, s, ownerId) => {
      const rows = checkSharing(body);
      if (typeof rows === "string") return validation(rows);
      s.sharing = rows;
      return reply(view(p, s, ownerId));
    }),

    "crews.addAgent": own("crews.addAgent", true, ({ body }, p, s, ownerId) => {
      if (!isObj(body)) return validation("body: expected an object");
      const extra = unknownKeys(body, ["name", "skill_pack_ids"]);
      if (extra.length) return validation(`${extra[0]}: unrecognized key`);
      const name = typeof body.name === "string" ? body.name.trim() : "";
      if (!name || name.length > 120) return validation("name: 1 to 120 characters");
      const packs = body.skill_pack_ids ?? [];
      if (
        !Array.isArray(packs) ||
        packs.length > 100 ||
        packs.some((x) => typeof x !== "string" || !UUID.test(x))
      )
        return validation("skill_pack_ids: a list of ids");
      const c = ceilingOf(p, ownerId);
      if ((packs as string[]).some((id) => !c.packs.has(id)))
        return validation("skill_pack_ids: no role skill policy of yours allows these packs");
      const added = s.agents.filter((a) => a.source === "owner_added" && a.enabled);
      if (added.length >= MAX_CREW_AGENTS)
        return problem(
          409,
          "conflict",
          "Conflict",
          `a crew has at most ${MAX_CREW_AGENTS} added agents`,
        );
      const t = at();
      const row: CrewAgent = {
        company_id: p.company.id,
        id: next(0xc7e3),
        crew_membership_id: s.crew.membership_id,
        agent_profile_id: next(0xc7e4),
        name,
        skill_pack_ids: packs as string[],
        source: "owner_added",
        enabled: true,
        ...writer(p),
        created_at: t,
        updated_at: t,
      };
      s.agents.push(row);
      return reply(row, 201);
    }),

    "crews.removeAgent": own("crews.removeAgent", true, ({ params }, p, s) => {
      const a = s.agents.find((x) => x.id === params.id && x.enabled);
      if (!a) return notFound();
      if (a.source === "role_default")
        return problem(
          409,
          "conflict",
          "Conflict",
          "the crew's own agent stays; pause the crew instead",
        );
      Object.assign(a, { enabled: false, ...writer(p), updated_at: at() });
      return reply(a);
    }),

    "crews.answerOnboarding": own("crews.answerOnboarding", true, ({ body }, p, s, ownerId) => {
      if (!isObj(body)) return validation("body: expected an object");
      const extra = unknownKeys(body, ["answers", "complete"]);
      if (extra.length) return validation(`${extra[0]}: unrecognized key`);
      if (!isObj(body.answers) || Object.keys(body.answers).length > 20)
        return validation("answers: at most 20 answers");
      for (const [k, v] of Object.entries(body.answers))
        if (typeof v !== "string" || v.length > 10_000)
          return validation(`answers.${k}: at most 10000 characters`);
      if (body.complete !== undefined && typeof body.complete !== "boolean")
        return validation("complete: expected a boolean");
      const complete = body.complete === true;
      const questions = view(p, s, ownerId).interview.questions;
      const unknown = Object.keys(body.answers).filter((k) => !questions.some((q) => q.id === k));
      if (unknown.length)
        return validation(
          unknown.map((k) => `answers.${k}: not a question of the interview`).join("; "),
        );
      const t = at();
      const state = s.crew.onboarding_state;
      const answers = { ...(state.answers ?? {}) };
      for (const [k, v] of Object.entries(body.answers as Record<string, string>))
        answers[k] = { answer: v, answered_at: t };
      s.crew.onboarding_state = {
        ...state,
        intake_version: 1,
        answers,
        answered_q: questions.map((q) => q.id).filter((id) => answers[id]?.answer.trim()),
        ...(complete ? (state.scaffolded_at ? { last_rerun_at: t } : { scaffolded_at: t }) : {}),
      };
      touch(p, s);
      if (complete) {
        const plain = Object.fromEntries(Object.entries(answers).map(([k, v]) => [k, v.answer]));
        for (const [layer, text] of layersFromAnswers(questions, plain))
          writeLayer(p, s, layer, text, "owner_only", "onboarding", layer);
      }
      return reply(view(p, s, ownerId));
    }),

    "crews.putContextLayer": own("crews.putContextLayer", true, ({ params, body }, p, s) => {
      const layer = params.layer as LayerName;
      if (!LAYERS.includes(layer)) return validation("layer: invalid option");
      if (!isObj(body)) return validation("body: expected an object");
      const extra = unknownKeys(body, ["body", "visibility"]);
      if (extra.length) return validation(`${extra[0]}: unrecognized key`);
      if (typeof body.body !== "string" || body.body.length > 20_000)
        return validation("body: at most 20000 characters");
      const visibility = (body.visibility ?? "owner_only") as Visibility;
      if (!VISIBILITIES.includes(visibility)) return validation("visibility: invalid option");
      return reply(writeLayer(p, s, layer, body.body, visibility, "owner", null));
    }),

    "crews.pause": own("crews.pause", true, (_ctx, p, s, ownerId) => {
      if (s.crew.state === "active") {
        s.crew.state = "paused";
        s.crew.pause_reason = "owner";
        touch(p, s);
      }
      return reply(view(p, s, ownerId));
    }),

    "crews.resume": own("crews.resume", true, (_ctx, p, s, ownerId) => {
      if (s.crew.state !== "paused") return reply(view(p, s, ownerId));
      if (s.crew.pause_reason === "membership") return problem(409, "conflict", "Conflict");
      const v = view(p, s, ownerId);
      if (v.caps.effective_minor !== null && v.caps.spent_minor >= v.caps.effective_minor)
        return problem(
          409,
          "budget_exhausted",
          "Budget exhausted",
          "Your agent's cap is reached for this period; raise it (up to the ceiling) first.",
        );
      const company = capsOf(p.company.id).company_cap_minor;
      if (company !== null && 0 >= company)
        return problem(
          409,
          "budget_exhausted",
          "Budget exhausted",
          "The company's cap for all agents is reached for this period.",
        );
      s.crew.state = "active";
      s.crew.pause_reason = null;
      touch(p, s);
      return reply(view(p, s, ownerId));
    }),

    // Admins: the ceiling -----------------------------------------------------------------------

    "roleSkillPolicies.list": managers("roleSkillPolicies.list", false, (_ctx, p) =>
      json({
        data: [...(policies.get(p.company.id) ?? [])].sort((a, b) =>
          a.role_id.localeCompare(b.role_id),
        ),
      }),
    ),

    "roleSkillPolicies.put": managers("roleSkillPolicies.put", true, ({ params, body }, p) => {
      const roleId = params.role_id ?? "";
      if (!UUID.test(roleId)) return validation("role_id: invalid UUID");
      if (!isObj(body)) return validation("body: expected an object");
      const extra = unknownKeys(body, [
        "allowed_skill_pack_ids",
        "default_skill_pack_ids",
        "budget_money_cents",
        "budget_period",
        "allowed_action_kinds",
      ]);
      if (extra.length) return validation(`${extra[0]}: unrecognized key`);
      const ids = (v: unknown, path: string): string[] | string =>
        Array.isArray(v) && v.length <= 100 && v.every((x) => typeof x === "string" && UUID.test(x))
          ? (v as string[])
          : `${path}: a list of ids`;
      const allowed = ids(body.allowed_skill_pack_ids ?? [], "allowed_skill_pack_ids");
      if (typeof allowed === "string") return validation(allowed);
      const defaults = ids(body.default_skill_pack_ids ?? [], "default_skill_pack_ids");
      if (typeof defaults === "string") return validation(defaults);
      const budget = body.budget_money_cents ?? null;
      if (budget !== null && (!isInt(budget) || (budget as number) < 0))
        return validation("budget_money_cents: a whole amount of zero or more");
      const period = body.budget_period ?? "month";
      if (!PERIODS.includes(period as never)) return validation("budget_period: invalid option");
      const kinds = body.allowed_action_kinds ?? [];
      if (!Array.isArray(kinds) || kinds.some((k) => !ACTION_KINDS.includes(k as ActionKind)))
        return validation("allowed_action_kinds: invalid option");
      if (!defaults.every((id) => allowed.includes(id)))
        return validation("default_skill_pack_ids: default_skill_pack_ids must be allowed");
      if (!deps.roleExists(p.company.id, roleId)) return notFound();
      const list = policies.get(p.company.id) ?? [];
      policies.set(p.company.id, list);
      const t = at();
      const values = {
        allowed_skill_pack_ids: allowed,
        default_skill_pack_ids: defaults,
        budget_money_cents: budget as number | null,
        budget_period: period as Policy["budget_period"],
        allowed_action_kinds: kinds as ActionKind[],
        ...writer(p),
        updated_at: t,
      };
      let row = list.find((x) => x.role_id === roleId);
      if (row) Object.assign(row, values);
      else {
        row = {
          company_id: p.company.id,
          id: next(0xc7e6),
          role_id: roleId,
          ...values,
          created_at: t,
        };
        list.push(row);
      }
      return reply(row);
    }),

    "roleSkillPolicies.delete": managers("roleSkillPolicies.delete", true, ({ params }, p) => {
      const list = policies.get(p.company.id) ?? [];
      const i = list.findIndex((x) => x.role_id === params.role_id);
      if (i >= 0) list.splice(i, 1);
      return new Response(null, { status: 204 });
    }),

    "crewCaps.get": managers("crewCaps.get", false, (_ctx, p) => json(capsOf(p.company.id))),

    "crewCaps.put": managers("crewCaps.put", true, ({ body }, p) => {
      if (!isObj(body)) return validation("body: expected an object");
      const extra = unknownKeys(body, ["per_crew_cap_minor", "company_cap_minor", "period_type"]);
      if (extra.length) return validation(`${extra[0]}: unrecognized key`);
      for (const k of ["per_crew_cap_minor", "company_cap_minor"] as const) {
        const v = body[k];
        if (v === undefined) return validation(`${k}: required`);
        if (v !== null && (!isInt(v) || (v as number) < 0))
          return validation(`${k}: a whole amount of zero or more`);
      }
      const period = body.period_type ?? "month";
      if (!PERIODS.includes(period as never)) return validation("period_type: invalid option");
      const t = at();
      const prev = caps.get(p.company.id);
      const row: Cap = {
        company_id: p.company.id,
        per_crew_cap_minor: body.per_crew_cap_minor as number | null,
        company_cap_minor: body.company_cap_minor as number | null,
        period_type: period as Cap["period_type"],
        ...writer(p),
        created_at: prev?.created_at ?? t,
        updated_at: t,
      };
      caps.set(p.company.id, row);
      return reply(row);
    }),

    // M4-03: the crew features (the owner's own) ---------------------------------------------

    "crews.putFeatures": own("crews.putFeatures", true, ({ body }, p, s, ownerId) => {
      const bad = checkFeatures(body);
      if (bad) return validation(bad);
      const b = body as {
        triage_enabled: boolean;
        brief: { enabled: boolean; time: string; days: number[] };
        heartbeat_schedule: string[];
      };
      Object.assign(s.crew, {
        triage_enabled: b.triage_enabled,
        brief_enabled: b.brief.enabled,
        brief_time: b.brief.time,
        brief_days: [...new Set(b.brief.days)].sort((x, y) => x - y),
        heartbeat_schedule: b.heartbeat_schedule.map((r) => r.trim()),
      });
      touch(p, s);
      return reply(view(p, s, ownerId));
    }),

    "crews.brief": own("crews.brief", true, (ctx, p, s, ownerId) => {
      if (s.crew.state !== "active") return crewPaused();
      const on = localClock(deps.now(), zoneOf(p)).date;
      const prior = s.briefs.get(on);
      if (prior) return reply(prior, 200);
      const text = composeBrief(briefInput(ctx, p, s, ownerId, on));
      const posted = deps.chat?.postBrief(
        chatRef(p, s),
        `Brief ${on}`,
        text,
        `brief:${s.crew.membership_id}:${on}`,
      );
      const brief: CrewBrief = {
        message_id: posted?.message_id ?? next(0xc7e7),
        thread_id: posted?.thread_id ?? next(0xc7e8),
        channel_id: s.crew.brief_channel_id,
        on,
        text,
      };
      s.briefs.set(on, brief);
      s.crew.last_brief_on = on;
      return reply(brief, 201);
    }),

    "crews.proposals": own("crews.proposals", false, (ctx, _p, s) => {
      const state = ctx.url.searchParams.get("state") ?? "pending";
      if (!PROPOSAL_STATES.includes(state as Proposal["state"]))
        return validation("state: invalid option");
      const items = expire(ctx, s);
      const rows = s.proposals
        .filter((x) => x.state === state)
        .sort((a, b) => b.created_at.localeCompare(a.created_at) || b.id.localeCompare(a.id))
        .slice(0, 50)
        .map((x) => withItem(items, x));
      return json({ data: rows });
    }),

    "crews.acceptProposal": own("crews.acceptProposal", true, (ctx, p, s, ownerId) => {
      expire(ctx, s);
      const x = s.proposals.find((y) => y.id === ctx.params.id);
      if (!x) return notFound();
      if (x.state !== "pending") return notPending(x);
      const refused = apply(ctx, p, s, ownerId, x);
      if (refused) return refused;
      Object.assign(x, {
        state: "accepted",
        decided_at: at(),
        decided_by_membership_id: ownerId,
        updated_at: at(),
      });
      return reply(withItem(deps.inbox?.list(ctx) ?? [], x));
    }),

    "crews.dismissProposal": own("crews.dismissProposal", true, (ctx, _p, s, ownerId) => {
      const items = expire(ctx, s);
      const x = s.proposals.find((y) => y.id === ctx.params.id);
      if (!x) return notFound();
      if (x.state !== "pending") return notPending(x);
      Object.assign(x, {
        state: "dismissed",
        decided_at: at(),
        decided_by_membership_id: ownerId,
        updated_at: at(),
      });
      return reply(withItem(items, x));
    }),

    "crews.reminders": own("crews.reminders", false, (_ctx, p, _s, ownerId) => {
      const seats = new Set(seatsOf(p, ownerId).map((x) => x.id));
      const now = deps.now();
      const t = now.toISOString();
      const since = new Date(now.getTime() - 24 * 3600_000).toISOString();
      const rows = remindersIn(p.company.id).filter((r) => seats.has(r.seat_id ?? ""));
      // `reminders.fire` (every minute): a due reminder fires once.
      for (const r of rows)
        if (r.fired_at === null && r.dismissed_at === null && r.at <= t)
          Object.assign(r, { fired_at: t, updated_at: t });
      return json({
        data: rows
          .filter((r) => r.dismissed_at === null && (r.fired_at === null || r.fired_at >= since))
          .sort((a, b) => a.at.localeCompare(b.at) || a.id.localeCompare(b.id))
          .map((r) => structuredClone(r)),
      });
    }),

    "crews.addReminder": own("crews.addReminder", true, ({ body }, p, _s, ownerId) => {
      if (!isObj(body)) return validation("body: expected an object");
      const extra = unknownKeys(body, ["text", "at", "seat_id"]);
      if (extra.length) return validation(`${extra[0]}: unrecognized key`);
      if (typeof body.text !== "string" || !body.text || body.text.length > CREW_TEXT)
        return validation(`text: 1 to ${CREW_TEXT} characters`);
      const when = instant(body.at);
      if (!when) return validation("at: an ISO date-time");
      if (when <= at()) return validation("at: must be in the future");
      const seats = seatsOf(p, ownerId);
      let seat: string | null = seats[0]?.id ?? null;
      if (body.seat_id !== undefined) {
        if (typeof body.seat_id !== "string" || !UUID.test(body.seat_id))
          return validation("seat_id: invalid UUID");
        if (!seats.some((x) => x.id === body.seat_id))
          return validation("seat_id: not a seat you hold");
        seat = body.seat_id;
      }
      if (!seat) return noSeat();
      return reply(addReminder(p, seat, body.text, when, "manual", null), 201);
    }),

    // ACTIONS: an open reminder's text or time (fired or dismissed: 409).
    "crews.updateReminder": own(
      "crews.updateReminder",
      true,
      ({ params, body }, p, _s, ownerId) => {
        const seats = new Set(seatsOf(p, ownerId).map((x) => x.id));
        const r = remindersIn(p.company.id).find(
          (x) => x.id === params.id && x.seat_id !== null && seats.has(x.seat_id),
        );
        if (!r) return notFound();
        if (!isObj(body)) return validation("body: expected an object");
        const extra = unknownKeys(body, ["text", "at"]);
        if (extra.length) return validation(`${extra[0]}: unrecognized key`);
        if (body.text === undefined && body.at === undefined) return validation("text or at");
        if (r.dismissed_at || r.fired_at)
          return problem(
            409,
            "conflict",
            "Conflict",
            `the reminder was ${r.dismissed_at ? "dismissed" : "fired"}; set a new one`,
          );
        if (body.text !== undefined) {
          if (typeof body.text !== "string" || !body.text || body.text.length > CREW_TEXT)
            return validation(`text: 1 to ${CREW_TEXT} characters`);
        }
        let when: string | null = null;
        if (body.at !== undefined) {
          when = instant(body.at);
          if (!when) return validation("at: an ISO date-time");
          if (when <= at()) return validation("at: must be in the future");
        }
        Object.assign(r, {
          ...(typeof body.text === "string" ? { text: body.text } : {}),
          ...(when ? { at: when } : {}),
          updated_at: at(),
        });
        return reply(structuredClone(r));
      },
    ),

    "crews.dismissReminder": own("crews.dismissReminder", true, ({ params }, p, _s, ownerId) => {
      const seats = new Set(seatsOf(p, ownerId).map((x) => x.id));
      const r = remindersIn(p.company.id).find(
        (x) => x.id === params.id && seats.has(x.seat_id ?? ""),
      );
      if (!r) return notFound();
      if (r.dismissed_at === null) Object.assign(r, { dismissed_at: at(), updated_at: at() });
      return reply(structuredClone(r));
    }),

    "crews.memoryGrants": own("crews.memoryGrants", false, (_ctx, p, s, ownerId) =>
      json(grantsView(p, s, ownerId)),
    ),

    // D12 consent step (services/api domain/memory/personal-grant.ts): the person's own session
    // only, not gated on the add-on as a whole (a grant can always be seen and taken back).
    "crews.personalMemory": (ctx) => {
      const p = ctx.principal;
      if (!p) return notFound();
      const m = personOf(p);
      if (!m) return forbidden("only a person's own session manages a crew");
      const s = crewOf(p, m.membership_id);
      const log = (personalLog.get(s.crew.membership_id) ?? []).slice().reverse().slice(0, 50);
      const unavailable = personalUnavailable();
      return json({
        agents: [
          {
            membership_id: s.crew.membership_id,
            name: s.name,
            access: log[0]?.access ?? "none",
            can_grant: unavailable === null,
            unavailable,
          },
        ],
        log,
      } satisfies OperationOutput<"crews.personalMemory">);
    },
    "crews.putPersonalMemory": (ctx) => {
      const p = ctx.principal;
      if (!p) return notFound();
      const b = ctx.body;
      if (
        !isObj(b) ||
        unknownKeys(b, ["access"]).length ||
        !["read", "none"].includes(String(b.access))
      )
        return validation("access: read or none");
      if (b.access === "read") {
        const off = gate(p, true);
        if (off) return off;
      }
      const m = personOf(p);
      if (!m) return forbidden("only a person's own session manages a crew");
      const s = crewOf(p, m.membership_id);
      return idempotent(ctx, "crews.putPersonalMemory", `${p.company.id}:${p.me.user.id}`, () => {
        const why = personalUnavailable();
        if (why !== null) return problem(409, "conflict", "Conflict", why);
        const access = b.access as "read" | "none";
        const entry = {
          at: at(),
          agent_membership_id: s.crew.membership_id,
          agent_name: s.name,
          access,
          effective: access === "read" ? "recall" : "none",
        };
        personalLog.set(s.crew.membership_id, [
          ...(personalLog.get(s.crew.membership_id) ?? []),
          entry,
        ]);
        return json({ access, effective: entry.effective });
      });
    },

    "crews.putMemoryGrants": own("crews.putMemoryGrants", true, ({ body }, p, s, ownerId) => {
      if (!isObj(body)) return validation("body: expected an object");
      const extra = unknownKeys(body, ["grants"]);
      if (extra.length) return validation(`${extra[0]}: unrecognized key`);
      if (!Array.isArray(body.grants) || body.grants.length > 32)
        return validation("grants: at most 32 entries");
      const asked: { bucket: Subject["bucket"]; subject_id: string }[] = [];
      for (const [i, g] of body.grants.entries()) {
        if (!isObj(g)) return validation(`grants.${i}: expected an object`);
        const bad = unknownKeys(g, ["bucket", "subject_id"]);
        if (bad.length) return validation(`grants.${i}.${bad[0]}: unrecognized key`);
        if (!BUCKETS.includes(g.bucket as Subject["bucket"]))
          return validation(`grants.${i}.bucket: invalid option`);
        if (typeof g.subject_id !== "string" || !UUID.test(g.subject_id))
          return validation(`grants.${i}.subject_id: invalid UUID`);
        asked.push({ bucket: g.bucket as Subject["bucket"], subject_id: g.subject_id });
      }
      const offered = available(p, ownerId);
      const keyOf = (g: { bucket: string; subject_id: string }) => `${g.bucket}:${g.subject_id}`;
      const refused = asked.filter((g) => !offered.some((a) => keyOf(a) === keyOf(g)));
      if (refused.length)
        return problem(
          422,
          "grant_not_allowed",
          "That memory is not the owner's to grant",
          refused.map((g) => `${g.bucket} ${g.subject_id}`).join(", "),
        );
      const want = new Set(asked.map(keyOf));
      const t = at();
      for (const g of s.grants)
        if (g.revoked_at === null && !want.has(keyOf(g)))
          Object.assign(g, { revoked_at: t, ...writer(p), updated_at: t });
      for (const key of want) {
        if (s.grants.some((g) => g.revoked_at === null && keyOf(g) === key)) continue;
        const subject = offered.find((a) => keyOf(a) === key);
        if (!subject) continue;
        s.grants.push({
          company_id: p.company.id,
          id: next(0xc7e9),
          crew_membership_id: s.crew.membership_id,
          bucket: subject.bucket,
          subject_kind: subject.subject_kind,
          subject_id: subject.subject_id,
          granted_by_membership_id: ownerId,
          revoked_at: null,
          ...writer(p),
          created_at: t,
          updated_at: t,
        });
      }
      return reply(grantsView(p, s, ownerId));
    }),

    // M7-05: hire my agent as a DE (domain/crews/hire.ts): a manager only; the memory groups must
    // be ones the crew holds and still the owner's to grant; the copy waits for Recall.
    "crews.hireAsDe": own("crews.hireAsDe", true, ({ body }, p, s, ownerId) => {
      if (!p.manager) return forbidden();
      const b = checkHire(body);
      if (typeof b === "string") return validation(b);
      const keyOf = (g: { bucket: string; subject_id: string }) => `${g.bucket}:${g.subject_id}`;
      const held = new Set(s.grants.filter((g) => g.revoked_at === null).map(keyOf));
      const offered = new Set(available(p, ownerId).map(keyOf));
      const asked = [...new Map(b.memory_groups.map((g) => [keyOf(g), g])).values()];
      const refused = asked.filter((g) => !held.has(keyOf(g)) || !offered.has(keyOf(g)));
      if (refused.length)
        return problem(
          422,
          "grant_not_allowed",
          "That memory is not the owner's to grant",
          refused.map((g) => `This ${g.bucket} memory is not one your agent holds`).join("; "),
        );
      if (!deps.hire) return notFound();
      const made = deps.hire(p, {
        display_name: b.display_name,
        template_key: b.template_key ?? "crew_hire",
        seat: b.seat,
        crew_membership_id: s.crew.membership_id,
      });
      if (made instanceof Response) return made;
      const t = at();
      const copy: MemoryCopy | null = asked.length
        ? {
            company_id: p.company.id,
            id: next(0xc0b1),
            de_membership_id: made.de_membership_id,
            crew_membership_id: s.crew.membership_id,
            target_seat_id: made.seat_id,
            sources: asked.map((g) => {
              const kind = g.bucket === "department" ? ("team" as const) : ("seat" as const);
              return {
                bucket: g.bucket,
                subject_kind: kind,
                subject_id: g.subject_id,
                external_id: `${kind}:${g.subject_id}`,
              };
            }),
            state: "pending",
            created: 0,
            reinforced: 0,
            proposed: 0,
            attempts: 0,
            last_error: null,
            copied_at: null,
            ...writer(p),
            created_at: t,
            updated_at: t,
          }
        : null;
      if (copy) copies.set(`${p.company.id}:${made.de_membership_id}`, copy);
      return reply({ ...made, copy: copy ? structuredClone(copy) : null }, 201);
    }),
  };

  // M7-05 helpers ------------------------------------------------------------------------------

  const copies = new Map<string, MemoryCopy>();

  function checkHire(b: unknown):
    | string
    | {
        display_name: string;
        template_key?: string;
        seat: HireRequest["seat"];
        memory_groups: { bucket: Subject["bucket"]; subject_id: string }[];
      } {
    if (!isObj(b)) return "body: expected an object";
    const extra = unknownKeys(b, ["display_name", "template_key", "seat", "memory_groups"]);
    if (extra.length) return `${extra[0]}: unrecognized key`;
    const name = (k: string, v: unknown) =>
      typeof v !== "string" || !v.trim() || v.length > 200 ? `${k}: 1 to 200 characters` : null;
    const bad = name("display_name", b.display_name);
    if (bad) return bad;
    if (b.template_key !== undefined) {
      const t = name("template_key", b.template_key);
      if (t) return t;
    }
    if (!isObj(b.seat)) return "seat: expected an object";
    const seatExtra = unknownKeys(b.seat, ["role_id", "title", "supervisor_seat_id"]);
    if (seatExtra.length) return `seat.${seatExtra[0]}: unrecognized key`;
    for (const k of ["role_id", "supervisor_seat_id"] as const)
      if (typeof b.seat[k] !== "string" || !UUID.test(b.seat[k] as string))
        return `seat.${k}: invalid UUID`;
    const title = name("seat.title", b.seat.title);
    if (title) return title;
    if (!Array.isArray(b.memory_groups) || b.memory_groups.length > 32)
      return "memory_groups: at most 32 entries";
    const groups: { bucket: Subject["bucket"]; subject_id: string }[] = [];
    for (const [i, g] of b.memory_groups.entries()) {
      if (!isObj(g)) return `memory_groups.${i}: expected an object`;
      const ge = unknownKeys(g, ["bucket", "subject_id"]);
      if (ge.length) return `memory_groups.${i}.${ge[0]}: unrecognized key`;
      if (!BUCKETS.includes(g.bucket as Subject["bucket"]))
        return `memory_groups.${i}.bucket: invalid option`;
      if (typeof g.subject_id !== "string" || !UUID.test(g.subject_id))
        return `memory_groups.${i}.subject_id: invalid UUID`;
      groups.push({ bucket: g.bucket as Subject["bucket"], subject_id: g.subject_id });
    }
    return {
      display_name: (b.display_name as string).trim(),
      ...(b.template_key !== undefined ? { template_key: (b.template_key as string).trim() } : {}),
      seat: {
        role_id: b.seat.role_id as string,
        title: (b.seat.title as string).trim(),
        supervisor_seat_id: b.seat.supervisor_seat_id as string,
      },
      memory_groups: groups,
    };
  }

  // M4-03 helpers -----------------------------------------------------------------------------

  const reminders = new Map<string, Reminder[]>();
  const remindersIn = (companyId: string) => {
    let list = reminders.get(companyId);
    if (!list) {
      list = [];
      reminders.set(companyId, list);
    }
    return list;
  };
  const seatsOf = (p: Principal, ownerId: string) => deps.ownerSeats?.(p, ownerId) ?? [];
  const zoneOf = (p: Principal) => p.company.calendar_config.timezone || "UTC";
  const crewPaused = () =>
    problem(409, "crew_paused", "Your agent is paused or deactivated", "Resume your agent first.");
  const noSeat = () =>
    problem(
      422,
      "no_seat",
      "The owner holds no seat to attach this to",
      "You hold no seat in this company; a reminder sits on a seat.",
    );
  const notPending = (x: Proposal) =>
    problem(409, "conflict", "Conflict", `the proposal is already ${x.state}`);
  /** A valid ISO date-time, normalised; null otherwise. */
  const instant = (v: unknown): string | null => {
    if (typeof v !== "string" || !/^\d{4}-\d{2}-\d{2}T/.test(v)) return null;
    const ms = Date.parse(v);
    return Number.isNaN(ms) ? null : new Date(ms).toISOString();
  };

  function addReminder(
    p: Principal,
    seatId: string,
    text: string,
    when: string,
    origin: Reminder["origin"],
    originRef: string | null,
  ): Reminder {
    const t = at();
    const row: Reminder = {
      company_id: p.company.id,
      id: next(0xc7ea),
      seat_id: seatId,
      membership_id: null,
      thread_id: null,
      at: when,
      text,
      subject_kind: null,
      subject_id: null,
      origin,
      origin_ref: originRef,
      fired_at: null,
      dismissed_at: null,
      created_at: t,
      updated_at: t,
    };
    remindersIn(p.company.id).push(row);
    return structuredClone(row);
  }

  function checkFeatures(b: unknown): string | null {
    if (!isObj(b)) return "body: expected an object";
    const extra = unknownKeys(b, ["triage_enabled", "brief", "heartbeat_schedule"]);
    if (extra.length) return `${extra[0]}: unrecognized key`;
    if (typeof b.triage_enabled !== "boolean") return "triage_enabled: expected a boolean";
    const br = b.brief;
    if (!isObj(br)) return "brief: expected an object";
    const bx = unknownKeys(br, ["enabled", "time", "days"]);
    if (bx.length) return `brief.${bx[0]}: unrecognized key`;
    if (typeof br.enabled !== "boolean") return "brief.enabled: expected a boolean";
    if (typeof br.time !== "string" || !HHMM.test(br.time)) return "brief.time: HH:MM";
    if (
      !Array.isArray(br.days) ||
      br.days.length > 7 ||
      br.days.some((d) => !isInt(d) || (d as number) < 1 || (d as number) > 7)
    )
      return "brief.days: ISO weekdays, 1 (Monday) to 7";
    const h = b.heartbeat_schedule;
    if (!Array.isArray(h) || h.length > 8) return "heartbeat_schedule: at most 8 entries";
    const problems: string[] = [];
    for (const [i, r] of h.entries()) {
      if (typeof r !== "string" || !r.trim() || r.length > 2000) {
        problems.push(`heartbeat_schedule.${i}: must not be empty`);
        continue;
      }
      const parsed = parseRule(r.trim());
      if (!parsed.ok) problems.push(`heartbeat_schedule.${i}: ${parsed.reason}`);
    }
    return problems.length ? problems.join("; ") : null;
  }

  /** What the brief reads, as the OWNER sees it (their inbox, reminders, proposals). */
  function briefInput(
    ctx: Ctx,
    p: Principal,
    s: CrewState,
    ownerId: string,
    on: string,
  ): BriefInput {
    const t = at();
    const zone = zoneOf(p);
    const items = expire(ctx, s);
    const open = items.filter(
      (i) => i.done_at === null && !(i.snoozed_until !== null && i.snoozed_until > t),
    );
    const unread = open.filter((i) => i.read_at === null);
    const seats = new Set(seatsOf(p, ownerId).map((x) => x.id));
    return {
      on,
      inbox: { unread: unread.length, top: unread.map((i) => i.summary) },
      // The twin's tasks sit on seats Work holds and its approvals on DEs' runs: the demo
      // owner holds no seat, so none is assigned to her and none waits on her decision.
      tasks: { dueOrOverdue: 0, top: [] },
      approvalsWaiting: 0,
      reminders: remindersIn(p.company.id)
        .filter(
          (r) =>
            seats.has(r.seat_id ?? "") &&
            r.dismissed_at === null &&
            localClock(new Date(r.at), zone).date === on,
        )
        .sort((a, b) => a.at.localeCompare(b.at))
        .map((r) => ({ text: r.text, at: localClock(new Date(r.at), zone).time })),
      proposalsPending: s.proposals.filter((x) => x.state === "pending").length,
    };
  }

  /** The owner's inbox; a pending proposal whose item is done or gone meanwhile expires. */
  function expire(ctx: Ctx, s: CrewState): InboxItem[] {
    const items = deps.inbox?.list(ctx) ?? [];
    const t = at();
    for (const x of s.proposals) {
      if (x.state !== "pending" || !x.inbox_item_id) continue;
      const item = items.find((i) => i.id === x.inbox_item_id);
      if (!item || item.done_at !== null)
        Object.assign(x, { state: "expired", decided_at: t, updated_at: t });
    }
    return items;
  }

  /** A proposal as the owner reads it, with its inbox item's summary. */
  function withItem(items: readonly InboxItem[], x: Proposal): Proposal {
    const i = x.inbox_item_id ? items.find((y) => y.id === x.inbox_item_id) : undefined;
    return {
      ...structuredClone(x),
      item: i
        ? {
            kind: i.kind,
            summary: i.summary,
            subject_kind: i.subject_kind,
            subject_id: i.subject_id,
          }
        : null,
    };
  }

  /** Applies a proposal (accept, or `alone`); a refusal is returned, null when applied. */
  function apply(
    ctx: Ctx,
    p: Principal,
    s: CrewState,
    ownerId: string,
    x: Proposal,
  ): Response | null {
    const item = x.inbox_item_id ?? "";
    switch (x.kind) {
      case "done":
        return deps.inbox?.done(ctx, item) === false ? notFound() : null;
      case "snooze":
        if ((x.snooze_until ?? "") <= at())
          return problem(409, "conflict", "Conflict", "the snooze time has passed");
        return deps.inbox?.snooze(ctx, item, x.snooze_until ?? "") === false ? notFound() : null;
      case "reply": {
        const posted = deps.chat?.postReply(
          chatRef(p, s),
          x.thread_id ?? "",
          x.payload.body_markdown ?? "",
          `crew-reply:${x.id}`,
        );
        return posted === null ? notFound() : null;
      }
      case "remind": {
        const seat = seatsOf(p, ownerId)[0]?.id;
        if (!seat) return noSeat();
        addReminder(p, seat, x.payload.text ?? "", x.remind_at ?? at(), "crew", x.id);
        return null;
      }
      case "task_update": {
        const change = {
          ...(x.payload.status !== undefined ? { status: x.payload.status } : {}),
          ...(x.payload.comment !== undefined ? { comment: x.payload.comment } : {}),
        };
        return deps.tasks?.update(ctx, x.task_id ?? "", change) === false
          ? forbidden("you cannot edit this task")
          : null;
      }
    }
  }

  /** The role memory the owner may grant (features.ts `grantableMemory`). */
  function available(p: Principal, ownerId: string): Subject[] {
    const seats = seatsOf(p, ownerId);
    const teams =
      deps.teamsOfSeats?.(
        p,
        seats.map((x) => x.id),
      ) ?? [];
    return [
      ...seats.map((x) => ({
        bucket: "seat" as const,
        subject_kind: "seat" as const,
        subject_id: x.id,
        label: `Seat: ${x.title}`,
      })),
      ...teams.map((t) => ({
        bucket: "department" as const,
        subject_kind: "team" as const,
        subject_id: t.id,
        label: `Department: ${t.name}`,
      })),
      ...seats
        .filter((x) => x.hasReports)
        .map((x) => ({
          bucket: "leadership" as const,
          subject_kind: "seat" as const,
          subject_id: x.id,
          label: `Leadership: ${x.title}`,
        })),
    ];
  }

  const grantsView = (p: Principal, s: CrewState, ownerId: string): Grants => ({
    data: s.grants.filter((g) => g.revoked_at === null).map((g) => structuredClone(g)),
    available: available(p, ownerId),
  });

  /** `crewRun.propose` (the crew's side): validation, then the gates in the design's order. */
  function checkProposal(b: unknown): string | ProposalBody {
    if (!isObj(b)) return "body: expected an object";
    const kind = b.kind as ProposalKind;
    const keys: Record<ProposalKind, string[]> = {
      done: ["inbox_item_id"],
      snooze: ["inbox_item_id", "snooze_until"],
      reply: ["inbox_item_id", "body_markdown"],
      remind: ["text", "remind_at"],
      task_update: ["task_id", "status", "comment"],
    };
    if (!(kind in keys)) return "kind: invalid option";
    const extra = unknownKeys(b, ["kind", "reason", ...keys[kind]]);
    if (extra.length) return `${extra[0]}: unrecognized key`;
    const reason = b.reason ?? "";
    if (typeof reason !== "string" || reason.length > CREW_TEXT)
      return `reason: at most ${CREW_TEXT} characters`;
    const id = (k: string) => (typeof b[k] === "string" && UUID.test(b[k] as string) ? b[k] : null);
    if (keys[kind].includes("inbox_item_id") && !id("inbox_item_id"))
      return "inbox_item_id: invalid UUID";
    switch (kind) {
      case "done":
        return { kind, inbox_item_id: b.inbox_item_id as string, reason };
      case "snooze": {
        const until = instant(b.snooze_until);
        if (!until) return "snooze_until: an ISO date-time";
        return { kind, inbox_item_id: b.inbox_item_id as string, snooze_until: until, reason };
      }
      case "reply": {
        const md = b.body_markdown;
        if (typeof md !== "string" || !md || md.length > CREW_DRAFT)
          return `body_markdown: 1 to ${CREW_DRAFT} characters`;
        return { kind, inbox_item_id: b.inbox_item_id as string, body_markdown: md, reason };
      }
      case "remind": {
        if (typeof b.text !== "string" || !b.text || b.text.length > CREW_TEXT)
          return `text: 1 to ${CREW_TEXT} characters`;
        const when = instant(b.remind_at);
        if (!when) return "remind_at: an ISO date-time";
        return { kind, text: b.text, remind_at: when, reason };
      }
      case "task_update": {
        if (!id("task_id")) return "task_id: invalid UUID";
        const status = b.status;
        const comment = b.comment;
        if (status !== undefined && (typeof status !== "string" || !status || status.length > 64))
          return "status: 1 to 64 characters";
        if (
          comment !== undefined &&
          (typeof comment !== "string" || !comment || comment.length > CREW_DRAFT)
        )
          return `comment: 1 to ${CREW_DRAFT} characters`;
        if (status === undefined && comment === undefined)
          return "status: a task update needs a status or a comment";
        return {
          kind,
          task_id: b.task_id as string,
          ...(status !== undefined ? { status: status as string } : {}),
          ...(comment !== undefined ? { comment: comment as string } : {}),
          reason,
        };
      }
    }
  }

  function propose(ctx: Ctx): Response {
    const p = ctx.principal;
    if (!p) return notFound();
    const off = gate(p, true);
    if (off) return off;
    const m = personOf(p);
    if (!m) return forbidden("only a person's crew proposes");
    const ownerId = m.membership_id;
    const s = crewOf(p, ownerId);
    if (s.crew.state !== "active") return crewPaused();
    const b = checkProposal(ctx.body);
    if (typeof b === "string") return validation(b);
    const t = at();
    const items = expire(ctx, s);
    let item: InboxItem | undefined;
    let threadId: string | null = null;
    if (b.kind === "done" || b.kind === "snooze" || b.kind === "reply") {
      item = items.find((i) => i.id === b.inbox_item_id && i.done_at === null);
      if (!item) return notFound();
      if (b.kind === "snooze" && b.snooze_until <= t)
        return validation("snooze_until: must be in the future");
      if (b.kind === "reply") {
        threadId = deps.chat?.threadOf(p.company.id, item.subject_kind, item.subject_id) ?? null;
        if (!threadId) return notFound();
      }
    }
    if (b.kind === "task_update" && deps.tasks?.visible(ctx, b.task_id) !== true) return notFound();
    // The gates, in order: the triage switch, then the owner's autonomy for the action kind.
    if (TRIAGE.has(b.kind) && !s.crew.triage_enabled)
      return problem(409, "triage_off", "Inbox triage is off for this agent");
    const auto = view(p, s, ownerId).autonomy.find(
      (a) => a.action_kind === PROPOSAL_ACTION[b.kind],
    );
    if (!auto || auto.mode === "never")
      return problem(403, "autonomy_never", "The owner does not allow this action");
    const alone = auto.mode === "alone" && auto.alone_allowed;
    if (item && s.proposals.some((x) => x.state === "pending" && x.inbox_item_id === item.id))
      return problem(409, "conflict", "Conflict", "the item already has a pending proposal");
    if (!alone && b.kind === "reply" && seatsOf(p, ownerId).length === 0) return noSeat();
    const row: Proposal = {
      company_id: p.company.id,
      id: next(0xc7eb),
      crew_membership_id: s.crew.membership_id,
      owner_membership_id: ownerId,
      kind: b.kind,
      state: "pending",
      inbox_item_id: item?.id ?? null,
      task_id: b.kind === "task_update" ? b.task_id : null,
      thread_id: threadId,
      approval_id: null,
      snooze_until: b.kind === "snooze" ? b.snooze_until : null,
      remind_at: b.kind === "remind" ? b.remind_at : null,
      payload:
        b.kind === "reply"
          ? { body_markdown: b.body_markdown }
          : b.kind === "remind"
            ? { text: b.text }
            : b.kind === "task_update"
              ? {
                  ...(b.status !== undefined ? { status: b.status } : {}),
                  ...(b.comment !== undefined ? { comment: b.comment } : {}),
                }
              : {},
      reason: b.reason,
      source: "run",
      run_id: next(0xc7ec),
      decided_at: null,
      decided_by_membership_id: null,
      actor_kind: "personal_agent",
      actor_membership_id: s.crew.membership_id,
      created_at: t,
      updated_at: t,
      item: null,
    };
    if (alone) {
      const refused = apply(ctx, p, s, ownerId, row);
      if (refused) return refused;
      row.state = "applied";
    }
    s.proposals.push(row);
    return json(withItem(deps.inbox?.list(ctx) ?? items, row), 201);
  }

  return {
    handlers,
    propose,
    copyOf: (companyId, deMembershipId) => {
      const c = copies.get(`${companyId}:${deMembershipId}`);
      return c ? structuredClone(c) : null;
    },
  };
}

type ProposalBody =
  | { kind: "done"; inbox_item_id: string; reason: string }
  | { kind: "snooze"; inbox_item_id: string; snooze_until: string; reason: string }
  | { kind: "reply"; inbox_item_id: string; body_markdown: string; reason: string }
  | { kind: "remind"; text: string; remind_at: string; reason: string }
  | { kind: "task_update"; task_id: string; status?: string; comment?: string; reason: string };

const PROPOSAL_STATES: readonly Proposal["state"][] = [
  "pending",
  "accepted",
  "dismissed",
  "applied",
  "expired",
];
